import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Clock } from 'lucide-react';
import { useClinic } from '@/stores/ClinicContext';
import { PatientService } from '@/services/firebase/patients';
import { formatCPF } from '@/utils/formatters';
import { PatientMetaLines } from '@/components/patients/PatientMetaLines';
import { addRecentPatient, getRecentPatientIds } from '@/utils/recentPatients';
import type { Patient } from '@/types/models';

/**
 * Global patient search bar with dropdown results.
 * Designed to sit in the top navigation bar across all pages.
 * Auto-detects CPF vs name input.
 *
 * `initialQuery` semeia o campo na montagem — usado pela gaveta da Agenda do
 * dia quando o médico sai da busca "só do dia" pra base inteira: o termo que
 * ele já tinha digitado vem junto, em vez de mandá-lo digitar de novo. Sem a
 * prop (uso da Topbar), o comportamento é exatamente o de antes.
 */
export const GlobalPatientSearch = ({ initialQuery = '' }: { initialQuery?: string } = {}) => {
  const navigate = useNavigate();
  const { clinicId } = useClinic();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<Patient[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState<Patient[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Refresh the "recently accessed" list — call on focus so newly-opened
  // patients bubble up even if this component wasn't unmounted in between.
  const loadRecent = useCallback(async () => {
    if (!clinicId) { setRecent([]); return; }
    const ids = getRecentPatientIds(clinicId);
    if (!ids.length) { setRecent([]); return; }
    try {
      const patients = await PatientService.getPatientsByIds(clinicId, ids);
      setRecent(patients);
    } catch {
      setRecent([]);
    }
  }, [clinicId]);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!clinicId) { setResults(null); return; }
    const trimmed = query.trim();
    if (trimmed.length < 2) { setResults(null); return; }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const digitsOnly = trimmed.replace(/\D/g, '');
        const looksLikeCpf = digitsOnly.length >= 2 && /^[\d.\-/]+$/.test(trimmed);
        const mode = looksLikeCpf ? 'cpf' : 'name';
        const res = await PatientService.searchPatients(clinicId, trimmed, mode);
        setResults(res);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, clinicId]);

  const handleSelect = (patientId: string) => {
    setOpen(false);
    setQuery('');
    setResults(null);
    if (clinicId) addRecentPatient(clinicId, patientId);
    navigate(`/pacientes/${patientId}`);
  };

  const handleRegister = () => {
    setOpen(false);
    const trimmed = query.trim();
    const digitsOnly = trimmed.replace(/\D/g, '');
    const looksLikeCpf = digitsOnly.length >= 2 && /^[\d.\-/]+$/.test(trimmed);
    setQuery('');
    setResults(null);
    // Navigate to patients page — the modal will open via state
    navigate('/pacientes', {
      state: looksLikeCpf
        ? { openNewPatient: true, initialCpf: digitsOnly }
        : { openNewPatient: true, initialName: trimmed },
    });
  };

  // Linha de um paciente no dropdown: nome + (CPF · nascimento). Usada tanto
  // nos "pesquisados recentemente" quanto nos resultados da busca.
  const renderPatientItem = (p: Patient) => (
    <li key={p.id}>
      <button
        type="button"
        onClick={() => handleSelect(p.id)}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-primary/10 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-primary-container text-primary flex items-center justify-center text-xs font-bold shrink-0">
          {p.name.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-on-surface truncate" title={p.name}>{p.name}</div>
          <PatientMetaLines p={p} incomplete={p.registrationStatus === 'MINIMAL'} />
        </div>
      </button>
    </li>
  );

  return (
    <div ref={wrapperRef} className="w-full relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-on-surface-variant pointer-events-none" />
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          // Refresh recents each time so just-visited patients rise to the top
          loadRecent();
          setOpen(true);
        }}
        placeholder="Buscar pacientes (nome ou CPF)..."
        className="w-full pl-9 py-2 pr-4 rounded-lg text-sm bg-surface-container text-on-surface placeholder-on-surface-variant outline-none transition-all duration-200 focus:bg-surface-container-high"
        style={{ border: '1px solid transparent' }}
      />

      {/* Dropdown */}
      {open && (query.trim().length < 2 ? recent.length > 0 : results !== null) && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-surface border border-outline-variant/20 rounded-xl shadow-lg overflow-hidden max-h-80 overflow-y-auto">
          {/* Recents shown when input is empty / not yet queryable (<2 chars) */}
          {query.trim().length < 2 && recent.length > 0 && (
            <>
              <div className="px-4 pt-3 pb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold text-on-surface-variant">
                <Clock size={12} />
                Pesquisados recentemente
              </div>
              <ul>
                {recent.map(renderPatientItem)}
              </ul>
            </>
          )}

          {results !== null && loading && (
            <div className="px-4 py-3 text-sm text-on-surface-variant animate-pulse">
              Buscando...
            </div>
          )}

          {results !== null && !loading && results.length === 0 && (() => {
            const trimmed = query.trim();
            const digitsOnly = trimmed.replace(/\D/g, '');
            const looksLikeCpf = digitsOnly.length >= 2 && /^[\d.\-/]+$/.test(trimmed);
            const buttonLabel = looksLikeCpf
              ? `Cadastrar CPF ${digitsOnly.length === 11 ? formatCPF(digitsOnly) : digitsOnly}`
              : `Cadastrar "${trimmed}"`;
            return (
              <div className="p-3 space-y-2">
                <p className="text-sm text-on-surface-variant text-center py-2">
                  Nenhum paciente encontrado.
                </p>
                {trimmed.length >= 2 && (
                  <button
                    type="button"
                    onClick={handleRegister}
                    className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-primary text-on-primary text-sm font-medium hover:brightness-110 transition-all"
                  >
                    <Plus size={16} />
                    {buttonLabel}
                  </button>
                )}
              </div>
            );
          })()}

          {results !== null && !loading && results.length > 0 && (
            <ul>
              {results.map(renderPatientItem)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
